'use client';

import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

import { useJourneyActions } from '~/stores/journeyStore';

const REFETCH_DELAYS_MS = [8000, 12000, 16500] as const;
const TOTAL_REFRESHES = 3;

interface JourneyRefetchOnProcessedProps {
  journeyId: string;
  startDate: string | null;
  shouldHidePriceInitially?: boolean;
  initialTotalPrice?: number;
}

export function JourneyRefetchOnProcessed({
  startDate,
  shouldHidePriceInitially,
  initialTotalPrice
}: JourneyRefetchOnProcessedProps) {
  const router = useRouter();
  const routerRef = useRef(router);
  routerRef.current = router;

  const { setIsCheckingAvailability } = useJourneyActions();
  const timeoutRefs = useRef<NodeJS.Timeout[]>([]);
  const initialPriceRef = useRef<number | undefined>(undefined);
  const [refreshCount, setRefreshCount] = useState(0);
  const hasRevealedPriceRef = useRef(false);
  const shouldHidePriceRef = useRef(shouldHidePriceInitially);

  shouldHidePriceRef.current = shouldHidePriceInitially;

  useEffect(() => {
    if (initialPriceRef.current === undefined && initialTotalPrice !== undefined) {
      initialPriceRef.current = initialTotalPrice;
    }
  }, [initialTotalPrice]);

  useEffect(() => {
    if (hasRevealedPriceRef.current) return;
    if (!shouldHidePriceRef.current) return;
    if (initialPriceRef.current === undefined) return;

    const priceChanged = initialTotalPrice !== initialPriceRef.current;
    const allRefreshesComplete = refreshCount >= TOTAL_REFRESHES;

    if (priceChanged || allRefreshesComplete) {
      hasRevealedPriceRef.current = true;
      setIsCheckingAvailability(false);
    }
  }, [initialTotalPrice, refreshCount, setIsCheckingAvailability]);

  useEffect(() => {
    if (!shouldHidePriceInitially || !startDate) {
      return;
    }

    let mounted = true;

    timeoutRefs.current = REFETCH_DELAYS_MS.map((delay) =>
      setTimeout(() => {
        if (!mounted) return;
        setRefreshCount((prev) => prev + 1);
        routerRef.current.refresh();
      }, delay)
    );

    return () => {
      mounted = false;
      timeoutRefs.current.forEach((id) => {
        clearTimeout(id);
      });
    };
  }, [shouldHidePriceInitially, startDate]);

  return null;
}
